// =====================================================================
//  ADMIN CONSOLE, PEOPLE (EMPLOYEE DIRECTORY)
// ---------------------------------------------------------------------
//  The system of record for everyone in the organization.
//
//  Search, filtering, sorting and paging are all done by the API (the new
//  `?search=&sort=&limit=&offset=` params), so this scales past the point
//  where fetching the whole table into the browser stops working.
//
//  A row opens a right-hand inspector rather than navigating away: the
//  administrator keeps their place in the list, and the person's
//  attendance, leave, payroll and documents are all one tab away.
// =====================================================================

import React, { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Clock,
  Download,
  FileText,
  Mail,
  MoreHorizontal,
  Pencil,
  Phone,
  Trash2,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import { useAdminAction } from '../CommandPalette';
import {
  Avatar,
  Button,
  Chip,
  ConfirmDialog,
  Drawer,
  EmptyState,
  Field,
  FilterChip,
  Grid,
  Input,
  KeyValue,
  LoadingBlock,
  Menu,
  MenuItem,
  MenuSeparator,
  Modal,
  PageHeader,
  PersonCell,
  RoleChip,
  SearchInput,
  Select,
  StatusChip,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import {
  DASH,
  downloadCsv,
  fmtDate,
  fmtDateTime,
  fmtHours,
  fmtMoney,
  fmtNumber,
  fmtTenure,
  orDash,
  toISODate,
} from '../format';

const ROLES = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
  { value: 'hr', label: 'HR' },
  { value: 'admin', label: 'Admin' },
];

const STATUSES = ['Active', 'Inactive', 'Onboarding', 'Terminated'];

// `department`/`position` are deliberately absent. The structure ids below
// are the source of truth since migration 0007, and a trigger on `users`
// fills the free-text columns from them. Sending both would mean the form
// could disagree with itself; sending only the id cannot.
const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  employee_id: '',
  department_id: '',
  designation_id: '',
  location_id: '',
  grade_id: '',
  role: 'employee',
  status: 'Active',
  join_date: '',
  salary: '',
  reporting_manager_id: '',
};

// Fields where a blank selection must be sent as an explicit clear rather
// than omitted from the payload.
const CLEARABLE_ON_EDIT = new Set([
  'department_id',
  'designation_id',
  'location_id',
  'grade_id',
  'reporting_manager_id',
]);

// Turns an org-structure table into <Select> options. Inactive records stay
// visible only when they are the current value, so editing someone parked in
// a retired department doesn't silently reassign them on save.
function structureOptions(rows, currentValue) {
  const current = currentValue === '' || currentValue === null ? null : String(currentValue);
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row.is_active !== false || String(row.id) === current)
    .map((row) => ({
      value: String(row.id),
      label: row.is_active === false ? `${row.name} (inactive)` : row.name,
    }));
}

export default function PeoplePage({ can, currentUser }) {
  const queryClient = useQueryClient();
  const canManage = can('employee.write');
  // Separate from canManage: managers may edit their team but must not see
  // pay. The server redacts the column regardless, it returns null for
  // salaries the caller may not read, so this only avoids rendering an
  // empty column that looks like missing data.
  const canSeeSalary = can('salary.read');

  /* ---------------------------- query state --------------------------- */
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [department, setDepartment] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sort, setSort] = useState({ key: 'full_name', dir: 'asc' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState([]);

  const debouncedSearch = useDebounced(search, 280);

  // Any filter change invalidates the current page number.
  const resetPage = useCallback(() => setPage(0), []);

  const queryParams = useMemo(
    () => ({
      search: debouncedSearch,
      status,
      department,
      role: roleFilter,
      sort: sort?.key,
      order: sort?.dir,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [debouncedSearch, status, department, roleFilter, sort, page, pageSize]
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'people', queryParams],
    queryFn: () => backendApi.entities.Employee.page(queryParams),
    // Keeps the previous page on screen while the next one loads, so the
    // table doesn't collapse to a skeleton on every keystroke.
    placeholderData: keepPreviousData,
  });

  // `data?.rows || []` is a new array identity on every render, which would
  // re-run every downstream memo each time. Pin it to the query result.
  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total || 0;

  // Department options come from the aggregate endpoint rather than the
  // current page, so the filter lists every department in the org.
  const { data: departments = [] } = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: () => backendApi.admin.departments(),
  });

  /* ------------------------ org structure lookups ---------------------- */
  // The employee form assigns structure by id, so it needs the four lookup
  // tables. They are small, change rarely and are readable by any
  // authenticated user, so they are cached for the session rather than
  // refetched per form open. `enabled: formOpen` would re-query on every
  // open; a long staleTime gets the same saving without the flicker.
  const lookupQuery = (entity) => ({
    queryKey: ['admin', 'structure', entity],
    queryFn: () => backendApi.entities[entity].list({ limit: 500 }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: departmentList = [] } = useQuery(lookupQuery('Department'));
  const { data: designationList = [] } = useQuery(lookupQuery('Designation'));
  const { data: locationList = [] } = useQuery(lookupQuery('Location'));
  const { data: gradeList = [] } = useQuery(lookupQuery('Grade'));

  // Candidate reporting managers. Everyone is a candidate, a lead can
  // report to a peer, but terminated staff are not, so the list is scoped
  // to people who are still here.
  const { data: managerPage } = useQuery({
    queryKey: ['admin', 'managerOptions'],
    queryFn: () =>
      backendApi.entities.Employee.page({
        status: 'Active',
        sort: 'full_name',
        order: 'asc',
        limit: 500,
      }),
    staleTime: 2 * 60 * 1000,
  });
  const managerCandidates = useMemo(() => managerPage?.rows || [], [managerPage]);

  /* ------------------------------ drawer ------------------------------ */
  const [activeId, setActiveId] = useState(null);
  const [drawerTab, setDrawerTab] = useState('profile');

  const openRecord = useCallback((id) => {
    setActiveId(id);
    setDrawerTab('profile');
  }, []);

  // Let the command palette open a person directly.
  useAdminAction('people:open', ({ id }) => openRecord(id));

  /* ------------------------------- form ------------------------------- */
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((person) => {
    setEditing(person);
    setForm({
      first_name: person.first_name || '',
      last_name: person.last_name || '',
      email: person.email || '',
      phone: person.phone || '',
      employee_id: person.employee_id || '',
      // Ids arrive as numbers; <Select> compares by string value.
      department_id: person.department_id ? String(person.department_id) : '',
      designation_id: person.designation_id ? String(person.designation_id) : '',
      location_id: person.location_id ? String(person.location_id) : '',
      grade_id: person.grade_id ? String(person.grade_id) : '',
      role: person.role || 'employee',
      status: person.status || 'Active',
      join_date: person.join_date ? String(person.join_date).slice(0, 10) : '',
      salary: person.salary ?? '',
      reporting_manager_id: person.reporting_manager_id || '',
    });
    setErrors({});
    setFormOpen(true);
  }, []);

  useAdminAction('people:new', openCreate);

  const setField = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  /* --------------------------- form options --------------------------- */
  const departmentOptions = useMemo(
    () => structureOptions(departmentList, form.department_id),
    [departmentList, form.department_id]
  );
  const designationOptions = useMemo(
    () => structureOptions(designationList, form.designation_id),
    [designationList, form.designation_id]
  );
  const locationOptions = useMemo(
    () => structureOptions(locationList, form.location_id),
    [locationList, form.location_id]
  );
  const gradeOptions = useMemo(
    () => structureOptions(gradeList, form.grade_id),
    [gradeList, form.grade_id]
  );

  // Nobody may be their own manager. The server rejects it too, but removing
  // the option is better than letting someone pick it and then be told no.
  const managerOptions = useMemo(() => {
    const selfId = editing?.id != null ? String(editing.id) : null;
    const options = managerCandidates
      .filter((person) => String(person.id) !== selfId)
      .map((person) => ({
        value: String(person.id),
        label: person.department ? `${person.full_name}, ${person.department}` : person.full_name,
      }));

    // The current manager may be inactive, or beyond the 500-row window.
    // Without this the select would render blank and a save would silently
    // clear a correct assignment.
    const current = form.reporting_manager_id ? String(form.reporting_manager_id) : '';
    if (current && !options.some((option) => option.value === current)) {
      options.unshift({ value: current, label: `Current manager (#${current})` });
    }
    return options;
  }, [managerCandidates, editing, form.reporting_manager_id]);

  const save = useMutation({
    mutationFn: (payload) =>
      editing
        ? backendApi.entities.Employee.update(editing.id, payload)
        : backendApi.entities.Employee.create(payload),
    onSuccess: () => {
      toast.success(editing ? 'Employee updated' : 'Employee added');
      setFormOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not save this employee'),
  });

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.first_name.trim()) nextErrors.first_name = 'First name is required';
    if (!form.email.trim()) nextErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) nextErrors.email = 'Enter a valid email';
    if (form.salary !== '' && Number.isNaN(Number(form.salary))) nextErrors.salary = 'Salary must be a number';

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    // Empty strings are dropped so the backend's null-coercion for date and
    // numeric columns takes over, except for the structure ids on an edit,
    // where "" is a deliberate instruction to clear the assignment. The
    // repository maps those to NULL (see NULLABLE_FK_COLUMNS). Dropping them
    // instead would make un-assigning a department impossible.
    const clearable = editing ? CLEARABLE_ON_EDIT : new Set();
    const payload = Object.fromEntries(
      Object.entries(form).filter(
        ([key, value]) =>
          (value !== '' && value !== null && value !== undefined) || clearable.has(key)
      )
    );
    save.mutate(payload);
  };

  /* ------------------------------ delete ------------------------------ */
  const [pendingDelete, setPendingDelete] = useState(null);

  const remove = useMutation({
    mutationFn: (id) => backendApi.entities.Employee.delete(id),
    onSuccess: () => {
      toast.success('Employee removed');
      setPendingDelete(null);
      setActiveId(null);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not remove this employee'),
  });

  const bulkDeactivate = useMutation({
    mutationFn: async (ids) => {
      // The API has no bulk endpoint, so this is a sequential fan-out. Doing
      // it one at a time (rather than Promise.all) keeps the connection pool
      // and the notification triggers from being hammered on a large select.
      const results = [];
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        results.push(await backendApi.entities.Employee.update(id, { status: 'Inactive' }));
      }
      return results;
    },
    onSuccess: (results) => {
      toast.success(`${results.length} employee${results.length === 1 ? '' : 's'} deactivated`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Bulk update failed'),
  });

  /* ------------------------------ export ------------------------------ */
  const exportCsv = useCallback(async () => {
    // Export the whole filtered set, not just the page on screen.
    const { rows: allRows } = await backendApi.entities.Employee.page({
      ...queryParams,
      limit: undefined,
      offset: undefined,
    });
    downloadCsv(
      `employees-${toISODate()}`,
      allRows,
      [
        { key: 'employee_id', label: 'Employee ID' },
        { key: 'full_name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'department', label: 'Department' },
        { key: 'position', label: 'Position' },
        { key: 'role', label: 'Role' },
        { key: 'status', label: 'Status' },
        { key: 'join_date', label: 'Join date' },
        { key: 'salary', label: 'Salary' },
      ]
    );
    toast.success(`Exported ${allRows.length} records`);
  }, [queryParams]);

  useAdminAction('people:export', exportCsv);

  /* ------------------------------ columns ----------------------------- */
  const columns = useMemo(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        width: 250,
        render: (row) => (
          <PersonCell name={row.full_name} email={row.email} sub={row.employee_id || row.email} />
        ),
        csv: (row) => row.full_name,
      },
      {
        key: 'department',
        label: 'Department',
        width: 150,
        render: (row) => orDash(row.department),
      },
      {
        key: 'position',
        label: 'Position',
        width: 160,
        render: (row) => orDash(row.position),
      },
      {
        key: 'role',
        label: 'Access',
        width: 100,
        render: (row) => <RoleChip role={row.role} />,
        csv: (row) => row.role,
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        render: (row) => <StatusChip value={row.status} />,
      },
      {
        key: 'join_date',
        label: 'Joined',
        width: 120,
        render: (row) => (
          <span title={row.join_date ? fmtTenure(row.join_date) : undefined}>{fmtDate(row.join_date)}</span>
        ),
      },
      {
        key: 'salary',
        label: 'Salary',
        width: 110,
        align: 'right',
        render: (row) => (canSeeSalary ? fmtMoney(row.salary) : DASH),
        csv: (row) => row.salary,
        defaultHidden: !canSeeSalary,
      },
      {
        key: 'email',
        label: 'Email',
        width: 210,
        defaultHidden: true,
        render: (row) => orDash(row.email),
      },
      {
        key: 'phone',
        label: 'Phone',
        width: 130,
        defaultHidden: true,
        render: (row) => orDash(row.phone),
      },
    ],
    [canSeeSalary]
  );

  // The command palette can open someone who isn't on the current page (or
  // isn't matched by the current filters at all), so fall back to fetching
  // that record directly instead of silently failing to open the drawer.
  const personOnPage = rows.find((r) => String(r.id) === String(activeId));

  const { data: fetchedPerson } = useQuery({
    queryKey: ['admin', 'person', activeId],
    queryFn: () => backendApi.entities.Employee.get(activeId),
    enabled: Boolean(activeId) && !personOnPage,
  });

  const activePerson = personOnPage || fetchedPerson;
  const filtersActive = Boolean(search || status || department || roleFilter);

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="People"
        sub="Everyone in your organization, their access level, and their employment record."
        actions={
          <>
            <Button icon={Download} onClick={exportCsv}>
              Export
            </Button>
            {canManage ? (
              <Button variant="primary" icon={UserPlus} onClick={openCreate}>
                Add employee
              </Button>
            ) : null}
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        total={total}
        loading={isLoading}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          resetPage();
        }}
        sort={sort}
        onSortChange={(next) => {
          setSort(next || { key: 'full_name', dir: 'asc' });
          resetPage();
        }}
        selectable={canManage}
        selected={selected}
        onSelectedChange={setSelected}
        onRowClick={(row) => openRecord(row.id)}
        emptyIcon={Users}
        emptyTitle={filtersActive ? 'No matching employees' : 'No employees yet'}
        emptyText={
          filtersActive
            ? 'Try a different search term or clear the filters.'
            : 'Add your first employee to start building the directory.'
        }
        bulkActions={(ids) => (
          <Button size="sm" onClick={() => bulkDeactivate.mutate(ids)} loading={bulkDeactivate.isPending}>
            Set inactive
          </Button>
        )}
        rowActions={
          canManage
            ? (row) => (
                <Menu
                  align="right"
                  trigger={({ toggle }) => (
                    <button type="button" className="ac-iconbtn" onClick={toggle} aria-label="Row actions" style={{ width: 24, height: 24 }}>
                      <MoreHorizontal size={14} strokeWidth={2} />
                    </button>
                  )}
                >
                  <MenuItem icon={FileText} onClick={() => openRecord(row.id)}>
                    View record
                  </MenuItem>
                  <MenuItem icon={Pencil} onClick={() => openEdit(row)}>
                    Edit
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem
                    icon={Trash2}
                    danger
                    disabled={String(row.id) === String(currentUser?.id)}
                    onClick={() => setPendingDelete(row)}
                  >
                    Remove
                  </MenuItem>
                </Menu>
              )
            : undefined
        }
        toolbar={
          <Toolbar>
            <SearchInput
              value={search}
              onChange={(value) => {
                setSearch(value);
                resetPage();
              }}
              placeholder="Search name, email, ID…"
              width={252}
            />
            <FilterChip
              label="Status"
              value={status}
              options={STATUSES}
              onChange={(value) => {
                setStatus(value);
                resetPage();
              }}
            />
            <FilterChip
              label="Department"
              value={department}
              options={departments.map((d) => d.department)}
              onChange={(value) => {
                setDepartment(value);
                resetPage();
              }}
            />
            <FilterChip
              label="Access"
              value={roleFilter}
              options={ROLES}
              onChange={(value) => {
                setRoleFilter(value);
                resetPage();
              }}
            />
            {filtersActive ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSearch('');
                  setStatus('');
                  setDepartment('');
                  setRoleFilter('');
                  resetPage();
                }}
              >
                Clear
              </Button>
            ) : null}
            <ToolbarSpacer />
            {isFetching ? <span className="ac-spinner" /> : null}
            <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
              {fmtNumber(total)} {total === 1 ? 'person' : 'people'}
            </span>
          </Toolbar>
        }
      />

      {/* ------------------------ RECORD DRAWER ------------------------ */}
      <EmployeeDrawer
        person={activePerson}
        open={Boolean(activeId)}
        onClose={() => setActiveId(null)}
        tab={drawerTab}
        onTabChange={setDrawerTab}
        canManage={canManage}
        onEdit={() => activePerson && openEdit(activePerson)}
        onDelete={() => activePerson && setPendingDelete(activePerson)}
        isSelf={String(activeId) === String(currentUser?.id)}
      />

      {/* --------------------------- FORM ----------------------------- */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? `Edit ${editing.full_name || 'employee'}` : 'Add an employee'}
        sub={
          editing
            ? 'Changes take effect immediately across the HRMS.'
            : 'The account is created active. They can sign in with the default password and will be prompted to change it.'
        }
        size="lg"
        footer={
          <>
            <Button onClick={() => setFormOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {editing ? 'Save changes' : 'Add employee'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit}>
          <Grid cols={2}>
            <Field label="First name" required error={errors.first_name}>
              <Input value={form.first_name} onChange={setField('first_name')} invalid={!!errors.first_name} autoFocus />
            </Field>
            <Field label="Last name">
              <Input value={form.last_name} onChange={setField('last_name')} />
            </Field>
            <Field label="Work email" required error={errors.email}>
              <Input
                type="email"
                value={form.email}
                onChange={setField('email')}
                invalid={!!errors.email}
                disabled={Boolean(editing)}
                hint={editing ? 'Email cannot be changed' : undefined}
              />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={setField('phone')} />
            </Field>
            <Field label="Employee ID" hint={editing ? undefined : 'Left blank, one is generated'}>
              <Input value={form.employee_id} onChange={setField('employee_id')} />
            </Field>
            <Field label="Join date">
              <Input type="date" value={form.join_date} onChange={setField('join_date')} />
            </Field>
            <Field
              label="Department"
              hint={departmentOptions.length ? undefined : 'Add departments under Organization first'}
            >
              <Select
                value={form.department_id}
                onChange={setField('department_id')}
                options={departmentOptions}
                placeholder="Unassigned"
                disabled={!departmentOptions.length}
              />
            </Field>
            <Field
              label="Designation"
              hint={designationOptions.length ? undefined : 'Add designations under Organization first'}
            >
              <Select
                value={form.designation_id}
                onChange={setField('designation_id')}
                options={designationOptions}
                placeholder="Unassigned"
                disabled={!designationOptions.length}
              />
            </Field>
            <Field label="Work location">
              <Select
                value={form.location_id}
                onChange={setField('location_id')}
                options={locationOptions}
                placeholder="Unassigned"
                disabled={!locationOptions.length}
              />
            </Field>
            <Field label="Grade" hint="Sets the salary band this person sits in">
              <Select
                value={form.grade_id}
                onChange={setField('grade_id')}
                options={gradeOptions}
                placeholder="Unassigned"
                disabled={!gradeOptions.length}
              />
            </Field>
            <Field label="Access level" hint="Controls what they can see and do">
              <Select value={form.role} onChange={setField('role')} options={ROLES} />
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={setField('status')} options={STATUSES} />
            </Field>
            <Field label="Monthly salary" error={errors.salary} hint="Used to calculate payroll">
              <Input
                type="number"
                min="0"
                step="1"
                value={form.salary}
                onChange={setField('salary')}
                invalid={!!errors.salary}
              />
            </Field>
            <Field label="Reporting manager">
              <Select
                value={form.reporting_manager_id}
                onChange={setField('reporting_manager_id')}
                options={managerOptions}
                placeholder="No manager"
              />
            </Field>
          </Grid>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate(pendingDelete.id)}
        loading={remove.isPending}
        title="Remove this employee?"
        confirmLabel="Remove permanently"
        message={
          <>
            <strong>{pendingDelete?.full_name}</strong> will be deleted along with their access to the HRMS. Their
            attendance, leave and payroll history will be detached rather than removed. This cannot be undone, consider setting them to <em>Inactive</em> instead if they may return.
          </>
        }
      />
    </div>
  );
}

/* =====================================================================
   RECORD DRAWER
   ---------------------------------------------------------------------
   Each tab loads only when it is opened, so viewing a person doesn't
   fire five queries for data the administrator may never look at.
   ================================================================== */

function EmployeeDrawer({ person, open, onClose, tab, onTabChange, canManage, onEdit, onDelete, isSelf }) {
  const tabs = [
    { key: 'profile', label: 'Profile' },
    { key: 'attendance', label: 'Attendance', icon: Clock },
    { key: 'leave', label: 'Leave', icon: CalendarDays },
    { key: 'payroll', label: 'Payroll', icon: Wallet },
    { key: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <Drawer
      open={open && Boolean(person)}
      onClose={onClose}
      title={person?.full_name || 'Employee'}
      sub={[person?.position, person?.department].filter(Boolean).join(' · ') || person?.email}
      badge={person ? <StatusChip value={person.status} /> : null}
      tabs={tabs}
      activeTab={tab}
      onTabChange={onTabChange}
      wide
      footer={
        canManage ? (
          <>
            <Button variant="danger" icon={Trash2} onClick={onDelete} disabled={isSelf}>
              Remove
            </Button>
            <div style={{ flex: '1 1 auto' }} />
            <Button onClick={onClose}>Close</Button>
            <Button variant="primary" icon={Pencil} onClick={onEdit}>
              Edit
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {!person ? null : tab === 'profile' ? (
        <ProfileTab person={person} />
      ) : tab === 'attendance' ? (
        <AttendanceTab person={person} />
      ) : tab === 'leave' ? (
        <LeaveTab person={person} />
      ) : tab === 'payroll' ? (
        <PayrollTab person={person} />
      ) : (
        <DocumentsTab person={person} />
      )}
    </Drawer>
  );
}

function ProfileTab({ person }) {
  // The record stores the manager as an id. Resolving it here, rather than
  // joining it into the directory query, keeps the one extra request on the
  // screen that actually displays it, and only for people who have a manager.
  const { data: manager } = useQuery({
    queryKey: ['admin', 'employee', person.reporting_manager_id],
    queryFn: () => backendApi.entities.Employee.get(person.reporting_manager_id),
    enabled: Boolean(person.reporting_manager_id),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
        <Avatar name={person.full_name} email={person.email} size="lg" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ac-ink)' }}>{person.full_name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--ac-muted)', marginTop: 1 }}>
            {person.position || 'No position set'}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
            <RoleChip role={person.role} />
            <StatusChip value={person.status} />
            {person.employee_id ? <Chip>{person.employee_id}</Chip> : null}
          </div>
        </div>
      </div>

      <SectionLabel>Contact</SectionLabel>
      <KeyValue
        items={[
          {
            key: 'Email',
            value: person.email ? (
              <a href={`mailto:${person.email}`} style={{ color: 'var(--ac-accent)', textDecoration: 'none' }}>
                <Mail size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                {person.email}
              </a>
            ) : null,
          },
          {
            key: 'Phone',
            value: person.phone ? (
              <a href={`tel:${person.phone}`} style={{ color: 'var(--ac-accent)', textDecoration: 'none' }}>
                <Phone size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                {person.phone}
              </a>
            ) : null,
          },
        ]}
      />

      <SectionLabel style={{ marginTop: 20 }}>Employment</SectionLabel>
      <KeyValue
        items={[
          { key: 'Employee ID', value: person.employee_id },
          { key: 'Department', value: person.department },
          { key: 'Position', value: person.position },
          { key: 'Access level', value: <RoleChip role={person.role} /> },
          { key: 'Joined', value: person.join_date ? `${fmtDate(person.join_date)} · ${fmtTenure(person.join_date)}` : null },
          { key: 'Monthly salary', value: person.salary ? fmtMoney(person.salary) : null },
          { key: 'Annual cost', value: person.salary ? fmtMoney(Number(person.salary) * 12) : null },
          {
            key: 'Reporting manager',
            // While the lookup is in flight, show nothing rather than the
            // raw id, KeyValue renders a dash for a null value, which reads
            // better than a number nobody can interpret.
            value: person.reporting_manager_id
              ? manager?.full_name || null
              : null,
          },
        ]}
      />

      <SectionLabel style={{ marginTop: 20 }}>Account</SectionLabel>
      <KeyValue
        items={[
          {
            key: 'Sign-in enabled',
            value: person.is_active ? <Chip tone="positive">Yes</Chip> : <Chip tone="warning">Never signed in</Chip>,
          },
          { key: 'Record created', value: fmtDateTime(person.created_at) },
        ]}
      />
    </>
  );
}

/** Shared loader for the drawer's history tabs. */
function useRecordsFor(entity, employeeId, extra = {}) {
  return useQuery({
    queryKey: ['admin', 'person-records', entity, employeeId, extra],
    queryFn: () =>
      backendApi.entities[entity].page({ employee_id: employeeId, limit: 60, ...extra }),
    enabled: Boolean(employeeId),
  });
}

function AttendanceTab({ person }) {
  const { data, isLoading } = useRecordsFor('Attendance', person.id, { sort: 'date', order: 'desc' });
  const rows = data?.rows || [];

  if (isLoading) return <LoadingBlock label="Loading attendance…" />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No attendance records"
        text="Days this person clocks in through the employee portal will appear here."
      />
    );
  }

  return (
    <MiniTable
      head={['Date', 'In', 'Out', 'Hours', 'Status']}
      rows={rows.map((r) => [
        fmtDate(r.date),
        orDash(r.check_in),
        orDash(r.check_out),
        r.hours_worked ? fmtHours(r.hours_worked) : DASH,
        <StatusChip value={r.status} key="s" />,
      ])}
      aligns={[null, null, null, 'right', null]}
    />
  );
}

function LeaveTab({ person }) {
  const { data, isLoading } = useRecordsFor('LeaveRequest', person.id, { sort: 'start_date', order: 'desc' });
  const rows = data?.rows || [];

  if (isLoading) return <LoadingBlock label="Loading leave…" />;
  if (rows.length === 0) {
    return <EmptyState icon={CalendarDays} title="No leave requests" text="Leave this person applies for will appear here." />;
  }

  return (
    <MiniTable
      head={['Type', 'From', 'To', 'Days', 'Status']}
      rows={rows.map((r) => [
        orDash(r.leave_type),
        fmtDate(r.start_date),
        fmtDate(r.end_date),
        orDash(r.days),
        <StatusChip value={r.status} key="s" />,
      ])}
      aligns={[null, null, null, 'right', null]}
    />
  );
}

function PayrollTab({ person }) {
  const { data, isLoading } = useRecordsFor('Payroll', person.id, { sort: 'year', order: 'desc' });
  const rows = data?.rows || [];

  if (isLoading) return <LoadingBlock label="Loading payslips…" />;
  if (rows.length === 0) {
    return <EmptyState icon={Wallet} title="No payslips" text="Payslips appear here once payroll has been run." />;
  }

  return (
    <MiniTable
      head={['Period', 'Gross', 'Deductions', 'Net', 'Status']}
      rows={rows.map((r) => [
        `${r.month} ${r.year}`,
        fmtMoney(r.gross_salary),
        fmtMoney(r.deductions),
        <strong key="n">{fmtMoney(r.net_salary)}</strong>,
        <StatusChip value={r.status} key="s" />,
      ])}
      aligns={[null, 'right', 'right', 'right', null]}
    />
  );
}

function DocumentsTab({ person }) {
  const { data, isLoading } = useRecordsFor('Document', person.id);
  const rows = data?.rows || [];

  if (isLoading) return <LoadingBlock label="Loading documents…" />;
  if (rows.length === 0) {
    return <EmptyState icon={FileText} title="No documents" text="Documents attached to this person will appear here." />;
  }

  return (
    <MiniTable
      head={['Title', 'Category', 'Uploaded', 'File']}
      rows={rows.map((r) => [
        orDash(r.title),
        r.category ? <Chip key="c">{r.category}</Chip> : DASH,
        fmtDate(r.created_at),
        r.file_url ? (
          <a key="f" href={r.file_url} target="_blank" rel="noreferrer" style={{ color: 'var(--ac-accent)' }}>
            Open
          </a>
        ) : (
          DASH
        ),
      ])}
    />
  );
}

/* --------------------------- drawer helpers -------------------------- */

function SectionLabel({ children, style }) {
  return (
    <div className="ac-eyebrow" style={{ marginBottom: 7, ...style }}>
      {children}
    </div>
  );
}

/** Compact read-only table used inside the drawer's history tabs. */
function MiniTable({ head, rows, aligns = [] }) {
  return (
    <div className="ac-tablewrap" style={{ border: '1px solid var(--ac-line)', borderRadius: 'var(--ac-r)' }}>
      <table className="ac-table">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={h} className={aligns[i] === 'right' ? 'ac-th--num' : undefined}>
                <span className="ac-th-inner">{h}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {cells.map((cell, cellIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={cellIndex} className={aligns[cellIndex] === 'right' ? 'ac-td--num' : undefined}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
